import { useMemo, useState } from 'react'
import { Box, Checkbox, Stack, Typography } from '@mui/material'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { useQuery } from '@tanstack/react-query'

import { api } from '../../../lib/api'
import { buildOrgTree, ORG_NODE_STYLE, type OrgNode, type OrgNodeKind } from '../../../lib/orgStructureTree'
import type { Customer } from '@cmms/api-client'
import type { Site } from '@cmms/types'

/// Group -> Sub Group -> Customer(-> Site) checkbox tree, for granting a
/// user's customer or site access. Reuses `buildOrgTree` (already built
/// for Settings -> Org Structure) rather than a flat list — a search box
/// alone doesn't help someone who wants "everything under this Sub
/// Group" on a tenant with hundreds of sites; checking a Group/Sub
/// Group/Customer row here selects every leaf underneath it in one
/// click (tri-state, mirrors the checkbox showing partial selection).
///
/// `customers`/`sites` are the CALLER's already-computed "unassigned"
/// lists (each section already excludes what the user already has) —
/// this component only re-fetches the groups tree, since neither
/// existing section fetches that today.
interface Props {
  mode: 'customer' | 'site'
  customers: Customer[]
  sites: Site[]
  selectedIds: Set<string>
  onChange: (ids: string[], checked: boolean) => void
  search: string
}

function collectLeafIds(node: OrgNode, mode: OrgNodeKind): string[] {
  if (node.kind === mode) return [node.id]
  return node.children.flatMap((c) => collectLeafIds(c, mode))
}

/// Drops every node that has no surviving selectable descendant — a
/// customer with zero unassigned sites left (site mode) or a Sub Group
/// with zero unassigned customers left (both modes) is dead weight in
/// a "here's what you can still grant" picker.
function pruneTree(nodes: OrgNode[], keepIds: Set<string>, mode: OrgNodeKind): OrgNode[] {
  const out: OrgNode[] = []
  for (const n of nodes) {
    if (n.kind === 'site') {
      if (mode === 'site' && keepIds.has(n.id)) out.push(n)
      continue
    }
    if (n.kind === 'customer') {
      if (mode === 'customer') {
        if (keepIds.has(n.id)) out.push({ ...n, children: [] })
      } else {
        const children = pruneTree(n.children, keepIds, mode)
        if (children.length > 0) out.push({ ...n, children })
      }
      continue
    }
    const children = pruneTree(n.children, keepIds, mode)
    if (children.length > 0) out.push({ ...n, children })
  }
  return out
}

export function OrgAccessTree({ mode, customers, sites, selectedIds, onChange, search }: Props) {
  const groupsQ = useQuery({
    queryKey: ['customer-groups-tree'],
    queryFn: () => api.customerGroups.tree(),
    staleTime: 5 * 60 * 1000,
  })
  const groups = groupsQ.data ?? []

  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  function toggleExpand(id: string) {
    setExpanded((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next })
  }

  const tree = useMemo(() => {
    const full = buildOrgTree(groups, customers, mode === 'site' ? sites : [])
    const keepIds = new Set(mode === 'customer' ? customers.map((c) => c.id) : sites.map((s) => s.id))
    return pruneTree(full, keepIds, mode)
  }, [groups, customers, sites, mode])

  const matchIds = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return null
    const hits = new Set<string>()
    function walk(nodes: OrgNode[]) {
      for (const n of nodes) { if (n.name.toLowerCase().includes(q)) hits.add(n.id); walk(n.children) }
    }
    walk(tree)
    const visible = new Set<string>()
    function mark(nodes: OrgNode[], ancestors: string[]): boolean {
      let any = false
      for (const n of nodes) {
        const childHit = mark(n.children, [...ancestors, n.id])
        if (hits.has(n.id) || childHit) { visible.add(n.id); ancestors.forEach((a) => visible.add(a)); any = true }
      }
      return any
    }
    mark(tree, [])
    return { hits, visible }
  }, [search, tree])

  if (groupsQ.isLoading) return null

  if (tree.length === 0) {
    return (
      <Typography sx={{ fontSize: 11.5, color: 'text.disabled', fontStyle: 'italic', px: 1.25, py: 1 }}>
        {search ? `No ${mode === 'customer' ? 'customers' : 'sites'} match "${search}".` : 'Nothing left to grant.'}
      </Typography>
    )
  }

  return (
    <Box>
      {tree.map((node) => (
        <AccessTreeRow key={node.id} node={node} depth={0} mode={mode}
          selectedIds={selectedIds} onChange={onChange}
          expanded={expanded} onToggleExpand={toggleExpand} matchIds={matchIds} />
      ))}
    </Box>
  )
}

function AccessTreeRow({ node, depth, mode, selectedIds, onChange, expanded, onToggleExpand, matchIds }: {
  node: OrgNode; depth: number; mode: OrgNodeKind
  selectedIds: Set<string>; onChange: (ids: string[], checked: boolean) => void
  expanded: Set<string>; onToggleExpand: (id: string) => void
  matchIds: { hits: Set<string>; visible: Set<string> } | null
}) {
  if (matchIds && !matchIds.visible.has(node.id)) return null
  const hasChildren = node.children.length > 0
  // Auto-expand every visible branch while a search is active — same
  // as OrgStructureTree — so a match three levels deep doesn't hide
  // behind a collapsed Group the user never touched.
  const isExpanded = expanded.has(node.id) || Boolean(matchIds)
  const { icon: Icon, color } = ORG_NODE_STYLE[node.kind]

  const leafIds = useMemo(() => collectLeafIds(node, mode), [node, mode])
  const checkedCount = leafIds.filter((id) => selectedIds.has(id)).length
  const allChecked = leafIds.length > 0 && checkedCount === leafIds.length
  const someChecked = checkedCount > 0 && !allChecked

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={0.5}
        onClick={() => hasChildren && onToggleExpand(node.id)}
        sx={{
          pl: 0.5 + depth * 2, pr: 1, py: 0.4, cursor: hasChildren ? 'pointer' : 'default',
          borderRadius: 1,
          '&:hover': { bgcolor: 'action.hover' },
        }}>
        <Box sx={{ width: 16, height: 16, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          color: 'text.secondary', flexShrink: 0, opacity: hasChildren ? 1 : 0,
          transform: isExpanded ? 'rotate(90deg)' : 'none', transition: 'transform 150ms ease' }}>
          <ChevronRightIcon sx={{ fontSize: 15 }} />
        </Box>
        <Checkbox
          size="small"
          checked={allChecked}
          indeterminate={someChecked}
          disabled={leafIds.length === 0}
          onClick={(e) => e.stopPropagation()}
          onChange={() => onChange(leafIds, !allChecked)}
          sx={{ p: 0.5 }}
        />
        <Box sx={{ width: 20, height: 20, borderRadius: 0.75, flexShrink: 0,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: `${color}1A`, color }}>
          <Icon sx={{ fontSize: 13 }} />
        </Box>
        <Typography sx={{ flex: 1, minWidth: 0, fontSize: 12.5, fontWeight: 500 }} noWrap>
          {node.name}
        </Typography>
        {hasChildren && (
          <Typography sx={{ fontSize: 10.5, fontWeight: 600, color: 'text.secondary', opacity: 0.7 }}>
            {checkedCount}/{leafIds.length}
          </Typography>
        )}
      </Stack>
      {hasChildren && isExpanded && node.children.map((child) => (
        <AccessTreeRow key={child.id} node={child} depth={depth + 1} mode={mode}
          selectedIds={selectedIds} onChange={onChange}
          expanded={expanded} onToggleExpand={onToggleExpand} matchIds={matchIds} />
      ))}
    </Box>
  )
}
